/** Parse money from API/form (strips commas, keeps decimals). */
export function parseQuotationMoney(value: unknown): number {
  if (value === null || value === undefined || value === "") return 0;
  const s = String(value).replace(/,/g, "").trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

/** Format for quotation UI, CSV, and PDF (always 2 decimal places). */
export function formatQuotationMoney(value: unknown): string {
  return parseQuotationMoney(value).toFixed(2);
}

/**
 * Plain PDF cell text (no zero-width spaces — those can show as gaps in article numbers).
 */
export function pdfCellText(value: unknown): string {
  const text = String(value ?? "").trim();
  return text || "-";
}

/**
 * @react-pdf/renderer hyphenation callback: wraps long unbroken values inside the column
 * without inserting visible gaps between digits.
 */
export function pdfHyphenationCallback(chunkSize = 10): (word: string) => string[] {
  const size = Math.max(4, Math.floor(chunkSize));
  return (word: string): string[] => {
    if (!word) return [""];
    const parts: string[] = [];
    let buf = "";
    for (let i = 0; i < word.length; i++) {
      const ch = word[i];
      buf += ch;
      // Break after separators, or when the run is long enough to need a wrap.
      if (/[/\\_\-.]/.test(ch) || buf.length >= size) {
        parts.push(buf);
        buf = "";
      }
    }
    if (buf) parts.push(buf);
    return parts.length > 0 ? parts : [word];
  };
}

/** @deprecated Prefer pdfCellText + pdfHyphenationCallback to avoid mid-digit gaps. */
export function wrapPdfCellText(value: unknown, _chunkSize = 16): string {
  return pdfCellText(value);
}

/** Strip leading bullet/check markers from payment-term lines for PDF display. */
export function cleanPaymentTermLine(line: string): string {
  return String(line || "")
    .trim()
    .replace(/^[✓•●▪◦–\-\*]+\s*/, "")
    .trim();
}

/** Fixing / discount from order API (with legacy fallbacks). */
export function getOrderFixingDiscount(o: Record<string, unknown> | null | undefined): {
  fixing: number;
  discount: number;
} {
  if (!o) return { fixing: 0, discount: 0 };
  let fixing = parseQuotationMoney(o.fixing ?? o.fixing_);
  let discount = parseQuotationMoney(o.discount ?? o.discount_);
  if (fixing > 0 || discount > 0) return { fixing, discount };
  const total = parseQuotationMoney(o.total);
  const subTotal = parseQuotationMoney(o.price);
  const gst = parseQuotationMoney(o.gst);
  const freight = parseQuotationMoney(o.freightCharges ?? o.transport ?? o.transport_);
  const impliedFixing = total - subTotal - gst - freight + discount;
  if (fixing <= 0 && impliedFixing > 0.009) fixing = impliedFixing;
  return { fixing, discount };
}

export type OrderNoteLike = {
  note?: string;
  createdBy?: string;
  created_by?: string;
  createdAt?: string;
  created_at?: string;
};

/** Oldest-first notes for CSV (Note 1 = first saved). */
export function sortOrderNotesChronological(notes: OrderNoteLike[]): OrderNoteLike[] {
  return [...notes].sort((a, b) => {
    const ta = parseApiDate(a.createdAt ?? a.created_at)?.getTime() ?? 0;
    const tb = parseApiDate(b.createdAt ?? b.created_at)?.getTime() ?? 0;
    return ta - tb;
  });
}

export function orderNoteText(n: OrderNoteLike | undefined): string {
  if (!n) return "";
  return String(n.note ?? "").trim();
}

export function orderNoteAuthor(n: OrderNoteLike | undefined): string {
  if (!n) return "";
  return String(n.createdBy ?? n.created_by ?? "").trim();
}

/** Parse API timestamps consistently. Legacy PostgreSQL timestamps without an offset are UTC. */
export function parseApiDate(value: unknown): Date | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const parsed = new Date(hasTimeZone ? raw : `${raw.replace(" ", "T")}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatIndiaDateTime(value: unknown): string {
  const date = parseApiDate(value);
  if (!date) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

/** Build Note 1..3 / By / Date & Time columns. */
export function buildCsvNoteColumns(
  activityNotes: OrderNoteLike[],
  quotationNotes?: string | null,
): string[] {
  const sorted = sortOrderNotesChronological(activityNotes);
  const slots: { note: string; by: string; dateTime: string }[] = [];
  const qn = String(quotationNotes ?? "").trim();
  if (qn && sorted.length === 0) {
    slots.push({ note: qn, by: "", dateTime: "" });
  }
  for (const n of sorted) {
    if (slots.length >= 3) break;
    slots.push({
      note: orderNoteText(n),
      by: orderNoteAuthor(n),
      dateTime: formatIndiaDateTime(n.createdAt ?? n.created_at),
    });
  }
  while (slots.length < 3) slots.push({ note: "", by: "", dateTime: "" });
  return slots.flatMap((s) => [s.note, s.by, s.dateTime]);
}

/** Normalize order.products from API (array, JSON string, or wrapped object). */
export function normalizeOrderProducts(order: { products?: unknown } | null | undefined): Record<string, unknown>[] {
  let raw = order?.products;
  if (raw == null) return [];
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      raw = JSON.parse(trimmed) as unknown;
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw)) {
    return raw.filter((p) => p != null && typeof p === "object") as Record<string, unknown>[];
  }
  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.items)) {
      return obj.items.filter((p) => p != null && typeof p === "object") as Record<string, unknown>[];
    }
    if (Array.isArray(obj.products)) {
      return obj.products.filter((p) => p != null && typeof p === "object") as Record<string, unknown>[];
    }
    return Object.values(obj).filter((p) => p != null && typeof p === "object") as Record<string, unknown>[];
  }
  return [];
}

/** Display name for CSV / exports (catalog + custom lines). */
export function getCsvProductName(product: Record<string, unknown> | null | undefined): string {
  if (!product) return "";
  const name = String(product.name ?? product.custom_name ?? "").trim();
  if (name) return name;
  return String(product.description ?? "").trim();
}

/** Order ID … Customer ID — product name must be the next column (index 5). */
export const QUOTATION_CSV_LEAD_COLUMNS = 5;

/** Order-level columns left blank on extra product rows (after unit price). */
export const QUOTATION_CSV_ORDER_DETAIL_BLANK_COUNT = 10;

/** Note 1 … Note 3 Date & Time */
export const QUOTATION_CSV_NOTE_COLUMN_COUNT = 9;

export function assertQuotationCsvRowWidth(row: unknown[], headerCount: number, label: string): void {
  if (row.length !== headerCount) {
    console.error(`Quotation CSV row "${label}": expected ${headerCount} columns, got ${row.length}`);
  }
}

/** Excel-safe CSV cell (no raw line breaks). */
export function quotationCsvCell(value: unknown): string {
  const s = String(value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\n+/g, " ")
    .trim();
  return `"${s.replace(/"/g, '""')}"`;
}

/** Cogent quotation PDF palette (softer blue, less dark) */
export const PDF_BRAND = "#2b4c7e";
export const PDF_BRAND_DARK = "#1f3658";
export const PDF_BRAND_BORDER = "#7ea6d8";
export const PDF_BRAND_SURFACE = "#f1f5fb";

/** Product table header: same fill as Grand Total bar */
export const PDF_TABLE_HEADER_BG = PDF_BRAND;
export const PDF_TABLE_HEADER_TEXT = "#ffffff";

/**
 * Page `paddingTop` (pt) so flow content clears the fixed **company** header band only
 * (details + letter + in-flow table header on the first quotation sheet).
 */
export const QUOTATION_PDF_PAGE_TOP_PADDING_PT = 138;

/**
 * Page `paddingTop` when the fixed band includes **company header + product table header**
 * (continuation quotation sheets — industry-style repeated column titles).
 */
export const QUOTATION_PDF_PAGE_WITH_TABLEHEAD_PADDING_PT = 168;

/** Conservative row counts for manual pagination (variable description height). */
export const QUOTATION_PDF_FIRST_PAGE_MAX_ROWS = 6;
export const QUOTATION_PDF_CONTINUATION_PAGE_MAX_ROWS = 12;

/** Split products for multi-page quotation tables (first chunk smaller due to address/letter block). */
export function chunkProductsForQuotationPdf<T>(items: T[], firstPageSize: number, continuationSize: number): T[][] {
  if (!items.length) return [[]];
  const chunks: T[][] = [];
  chunks.push(items.slice(0, firstPageSize));
  let pos = firstPageSize;
  while (pos < items.length) {
    chunks.push(items.slice(pos, pos + continuationSize));
    pos += continuationSize;
  }
  return chunks;
}

/** Split a long comma-separated address into two balanced lines for PDF headers. */
export function splitCompanyAddressIntoTwoLines(address: string): {
  line1: string;
  line2?: string;
} {
  const trimmed = (address || "").trim();
  if (!trimmed) return { line1: "" };
  const parts = trimmed
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length <= 1) return { line1: trimmed };
  const mid = Math.ceil(parts.length / 2);
  return {
    line1: parts.slice(0, mid).join(", "),
    line2: parts.slice(mid).join(", "),
  };
}

/** Quotation PDF signatory fields from API payloads (camelCase and legacy/snake keys). */
export function getQuotationSignatory(order: unknown): { name: string; mobile: string } {
  const o = order as Record<string, unknown> | null | undefined;
  if (!o) return { name: "", mobile: "" };
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = o[k];
      if (v == null) continue;
      const s = String(v).trim();
      if (s) return s;
    }
    return "";
  };
  return {
    name: pick("quotationSignatoryName", "quotation_signatory_name", "quotation_signatory_name_"),
    mobile: pick("quotationSignatoryMobile", "quotation_signatory_mobile", "quotation_signatory_mobile_"),
  };
}

/** GST summary row for quotation PDF grand-total panel. */
export type QuotationGstSummaryRow = { key: string; label: string; amount: number };

/** Intrastate (CGST+SGST), interstate (IGST at full slab %), or single GST line for non-India. */
export type QuotationGstKind = "intrastate" | "interstate" | "foreign";

function normPlace(s: string): string {
  return (s || "").trim().toLowerCase().replace(/\s+/g, " ");
}

export function resolveQuotationGstKind(
  customerCountry: string,
  companyCountry: string,
  customerState: string,
  companyState: string,
): QuotationGstKind {
  if (normPlace(customerCountry) !== "india" || normPlace(companyCountry) !== "india") {
    return "foreign";
  }
  const cs = normPlace(customerState);
  const cc = normPlace(companyState);
  if (cs && cc && cs === cc) return "intrastate";
  return "interstate";
}

/**
 * Build GST line items for the summary table from taxable bases per GST rate (%).
 * Intrastate India: CGST (rate/2)% + SGST (rate/2)%.
 * Interstate India: IGST at full slab rate (rate)% on taxable base.
 * Non-India: one line per slab "GST (rate%)".
 */
export function buildQuotationGstSummaryRows(
  gstRateMap: Record<number, number>,
  kind: QuotationGstKind,
): QuotationGstSummaryRow[] {
  const slabs = Object.entries(gstRateMap)
    .map(([rateStr, base]) => {
      const rate = parseFloat(rateStr);
      const totalTax = parseFloat(((base * rate) / 100).toFixed(2));
      return { rate, base, totalTax };
    })
    .filter((x) => x.rate > 0 && x.totalTax > 0)
    .sort((a, b) => a.rate - b.rate);

  const out: QuotationGstSummaryRow[] = [];
  for (const { rate, base, totalTax } of slabs) {
    const halfRate = rate / 2;
    if (kind === "intrastate") {
      const cgst = parseFloat(((base * halfRate) / 100).toFixed(2));
      const sgst = parseFloat(((base * halfRate) / 100).toFixed(2));
      out.push({ key: `cgst-${rate}`, label: `CGST (${halfRate}%)`, amount: cgst });
      out.push({ key: `sgst-${rate}`, label: `SGST (${halfRate}%)`, amount: sgst });
    } else if (kind === "interstate") {
      out.push({ key: `igst-${rate}`, label: `IGST (${rate}%)`, amount: totalTax });
    } else {
      out.push({ key: `gst-${rate}`, label: `GST (${rate}%)`, amount: totalTax });
    }
  }
  return out;
}

/**
 * GST rows for freight charges (same split rules as products: CGST+SGST intrastate, IGST interstate, single GST foreign).
 */
export function buildFreightGstSummaryRows(
  freightBase: number,
  gstPercent: number,
  kind: QuotationGstKind,
): QuotationGstSummaryRow[] {
  if (freightBase <= 0 || gstPercent <= 0) return [];
  const rate = gstPercent;
  const totalTax = parseFloat(((freightBase * rate) / 100).toFixed(2));
  if (totalTax <= 0) return [];
  const halfRate = rate / 2;
  if (kind === "intrastate") {
    const cgst = parseFloat(((freightBase * halfRate) / 100).toFixed(2));
    const sgst = parseFloat(((freightBase * halfRate) / 100).toFixed(2));
    return [
      { key: "freight-cgst", label: `CGST on freight (${halfRate}%)`, amount: cgst },
      { key: "freight-sgst", label: `SGST on freight (${halfRate}%)`, amount: sgst },
    ];
  }
  if (kind === "interstate") {
    return [{ key: "freight-igst", label: `IGST on freight (${rate}%)`, amount: totalTax }];
  }
  return [{ key: "freight-gst", label: `GST (${rate}% on freight)`, amount: totalTax }];
}

/** Fold freight taxable base into the product GST rate map so freight tax lands in CGST/SGST/IGST lines. */
export function appendFreightToGstRateMap(
  gstRateMap: Record<number, number>,
  freightBase: number,
  freightGstPercent: number,
): Record<number, number> {
  const map: Record<number, number> = { ...gstRateMap };
  if (freightBase > 0 && freightGstPercent > 0) {
    map[freightGstPercent] = (map[freightGstPercent] || 0) + freightBase;
  }
  return map;
}

/** Default warranty clause for new quotations / PDF Terms §8. */
export const DEFAULT_WARRANTY =
  "12 months from the date of supply only for manufacturing defects.";

/** Warranty text shown in PDF Terms & Conditions §8. */
export function getWarrantyDisplay(order: unknown): string {
  const o = order as Record<string, unknown> | null | undefined;
  if (!o) return DEFAULT_WARRANTY;
  for (const k of ["warranty", "warranty_"]) {
    const v = o[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return DEFAULT_WARRANTY;
}

/**
 * Title for the freight row in PDF / order UI totals (not part of the product GST breakdown).
 * Shows taxable base + GST %; the amount beside it should be base + GST on freight.
 */
/** Default text for Terms → Freight Charges when none is saved. */
export const DEFAULT_FREIGHT_NOTES = "EXTRA";

/** Freight note shown in PDF Terms & Conditions §3 (freight charges). */
export function getFreightNotesDisplay(order: unknown): string {
  const o = order as Record<string, unknown> | null | undefined;
  if (!o) return DEFAULT_FREIGHT_NOTES;
  for (const k of ["freightNotes", "freight_notes_"]) {
    const v = o[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return DEFAULT_FREIGHT_NOTES;
}

export function formatFreightChargesLineLabel(freightBase: number, gstPercent: number): string {
  if (!(freightBase > 0)) return "Freight charges";
  const baseStr = freightBase.toFixed(2);
  if (gstPercent > 0) return `Freight`;
  return "Freight charges";
}

/** Human-readable supply type for exports (CSV, etc.). */
export function quotationGstSupplyTypeLabel(kind: QuotationGstKind): string {
  if (kind === "intrastate") return "Intra-state (India)";
  if (kind === "interstate") return "Inter-state (India)";
  return "Overseas / non-India";
}

/** Roll up PDF-style GST rows into fixed columns for flat exports. */
export function aggregateQuotationGstSummaryAmounts(rows: QuotationGstSummaryRow[]): {
  cgst: number;
  sgst: number;
  igst: number;
  overseasGst: number;
  total: number;
} {
  let cgst = 0;
  let sgst = 0;
  let igst = 0;
  let overseasGst = 0;
  for (const r of rows) {
    if (r.label.startsWith("CGST")) cgst += r.amount;
    else if (r.label.startsWith("SGST")) sgst += r.amount;
    else if (r.label.startsWith("IGST")) igst += r.amount;
    else if (r.label.startsWith("GST (")) overseasGst += r.amount;
  }
  const total = rows.reduce((s, r) => s + r.amount, 0);
  return { cgst, sgst, igst, overseasGst, total };
}
